import { useEffect, useRef, useState } from "react";
import Spinner from "../spinner/spinner";
import { LOCALE } from "../../locales";
import { track, TRACK } from "../../libs/teads-tracking";
import "./start-screen.css";
import posterPan from "../../assets/images/pan-loop.webp";
import posterPot from "../../assets/images/bowl-loop.webp";
import videoPan from "../../assets/videos/pan.loop.mp4";
import videoPot from "../../assets/videos/bowl.loop.mp4";
import videoPanH264 from "../../assets/videos/pan.loop.h264.mp4";
import videoPotH264 from "../../assets/videos/bowl.loop.h264.mp4";
import logoBox from "../../assets/miele-logo-box.webp";
import selectorPan from "../../assets/selector-pan.webp";
import selectorPot from "../../assets/selector-pot.webp";
import arIcon from "../../assets/ar-icon.webp";

const strings = LOCALE.strings;

// Each background is a short clip that loops seamlessly. The delivered clips
// (pan.mp4 / bowl.mp4) do not end on the framing they start with, so the
// *.loop.mp4 versions have their last frames cross-faded into their first
// ones (scripts/make-loop-clips.sh); the posters are those clips' first
// frame. The clips are HEVC, which Safari and most Android browsers decode in
// hardware; the .h264 copies are for browsers without HEVC.
const PRODUCTS = [
  {
    id: "pan",
    poster: posterPan,
    video: videoPan,
    videoFallback: videoPanH264,
    selector: selectorPan,
    label: strings.pan,
  },
  {
    id: "pot",
    poster: posterPot,
    video: videoPot,
    videoFallback: videoPotH264,
    selector: selectorPot,
    label: strings.pot,
  },
];

// Matches the .start-bg opacity transition: the outgoing clip keeps playing
// until it is fully hidden.
const SWITCH_FADE_MS = 450;

// React reflects `muted` as a property rather than an attribute, and only
// muted video may autoplay on mobile, so make sure of it before every play().
function playMuted(video) {
  video.muted = true;
  video.defaultMuted = true;
  const playing = video.play();
  return playing && playing.then ? playing : Promise.resolve();
}

const StartScreen = ({ started, onStart }) => {
  const [productId, setProductId] = useState("pan");
  // idle -> (tap) launching -> loading; the launch animation plays between
  // the View in AR button and the Loading row.
  const [launching, setLaunching] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  // Per product: true once autoplay was refused (poster only from then on).
  const [unavailable, setUnavailable] = useState({});
  // The logo entrance starts only once the webp is decoded: on a cold iOS
  // load the image often arrives after a mount-time animation has already
  // run, which degraded the drop into a plain fade-in.
  const [logoReady, setLogoReady] = useState(false);
  const videoRefs = useRef({});

  useEffect(() => {
    if (!launching) return;
    const timer = setTimeout(() => {
      setLaunching(false);
      setShowLoading(true);
    }, 560);
    return () => clearTimeout(timer);
  }, [launching]);

  // safety net: if the camera got started without our click flow, show loading
  useEffect(() => {
    if (started && !launching) setShowLoading(true);
  }, [started, launching]);

  // Only the selected product's clip plays; the other one is paused once the
  // cross-fade away from it has finished.
  useEffect(() => {
    const timers = [];
    PRODUCTS.forEach((product) => {
      const video = videoRefs.current[product.id];
      if (!video) return;
      if (product.id !== productId) {
        timers.push(setTimeout(() => video.pause(), SWITCH_FADE_MS));
        return;
      }
      playMuted(video)
        .then(() =>
          setUnavailable((prev) =>
            prev[product.id] ? { ...prev, [product.id]: false } : prev,
          ),
        )
        .catch(() => {
          // Autoplay refused (iOS Low Power Mode, data saver): the
          // poster stays as a still background. A later selector tap
          // is a user gesture, so the next play() gets another chance.
          setUnavailable((prev) => ({ ...prev, [product.id]: true }));
        });
    });
    return () => timers.forEach(clearTimeout);
  }, [productId]);

  function handleStart() {
    if (launching || showLoading || started) return;
    setLaunching(true);
    track(TRACK.AR);
    // must stay inside the tap's user-activation window (iOS motion permission)
    onStart();
  }

  return (
    <div className="start-screen">
      {PRODUCTS.map((product) => (
        <div
          key={product.id}
          className={"start-bg" + (productId === product.id ? " visible" : "")}
        >
          <img className="start-bg-poster" src={product.poster} alt="" />
          <video
            ref={(el) => {
              videoRefs.current[product.id] = el;
            }}
            className={
              "start-bg-video" + (unavailable[product.id] ? " unavailable" : "")
            }
            poster={product.poster}
            autoPlay={product.id === productId}
            muted
            loop
            playsInline
            preload="auto"
          >
            <source
              src={product.video}
              type='video/mp4; codecs="hvc1.1.6.L93.B0"'
            />
            <source
              src={product.videoFallback}
              type='video/mp4; codecs="avc1.640029"'
            />
          </video>
        </div>
      ))}
      <img
        className={"start-logo" + (logoReady ? " ready" : "")}
        src={logoBox}
        alt="Miele"
        onLoad={() => setLogoReady(true)}
        ref={(el) => {
          if (el && el.complete) setLogoReady(true);
        }}
      />
      <div className="start-heading">
        <h1 className="start-headline">
          <span>{strings.headline1}</span>
          <span>{strings.headline2}</span>
        </h1>
        <span className="start-subline">{strings.subline}</span>
      </div>
      {PRODUCTS.map((product) => (
        <button
          key={product.id}
          type="button"
          className={
            "product-button " +
            product.id +
            (productId === product.id ? " selected" : "")
          }
          onClick={() => {
            setProductId(product.id);
            track(product.id === "pan" ? TRACK.AD_PAN : TRACK.AD_POT);
          }}
          aria-label={product.label}
        >
          <img src={product.selector} alt="" />
        </button>
      ))}
      {showLoading ? (
        <div className="ar-action loading">
          <span className="ar-action-inner">
            <Spinner size={50} color="white" />
            <span className="ar-action-label" aria-label={strings.loading}>
              {strings.loading.split("").map((ch, i) => (
                <span
                  key={i}
                  className="ar-letter"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  aria-hidden="true"
                >
                  {ch}
                </span>
              ))}
            </span>
          </span>
        </div>
      ) : (
        <button
          type="button"
          className={"ar-action" + (launching ? " launching" : "")}
          onClick={handleStart}
        >
          <span className="ar-action-inner">
            <span className="ar-icon-wrap">
              <span className="ar-ping" aria-hidden="true"></span>
              <img className="ar-action-icon" src={arIcon} alt="" />
            </span>
            {/* letter-by-letter wave, same recipe as the Sephora placeholder wave */}
            <span className="ar-action-label" aria-label={strings.viewInAr}>
              {strings.viewInAr.split("").map((ch, i) => (
                <span
                  key={i}
                  className="ar-letter"
                  style={{ animationDelay: `${i * 0.08}s` }}
                  aria-hidden="true"
                >
                  {ch === " " ? " " : ch}
                </span>
              ))}
            </span>
          </span>
        </button>
      )}
      <a
        className="start-shop-now"
        href={LOCALE.shopUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => track(TRACK.CTA)}
      >
        <img src={strings.learnMoreImg} alt={strings.learnMore} />
      </a>
    </div>
  );
};

export default StartScreen;
